import { Link } from "react-router-dom";
import { useSettings, useServices, useAreas } from "../lib/hooks";
import { telLink, track } from "../lib/api";
import { Phone, Mail, ArrowUpRight } from "lucide-react";

export default function Footer() {
  const { data: settings } = useSettings();
  const { data: services } = useServices();
  const { data: areas } = useAreas();
  const year = new Date().getFullYear();
  const GROUP_ORDER = ["Bristol", "Bath & Around", "South Gloucestershire", "North Somerset", "Somerset", "South Wales", "Devon"];
  const grouped = GROUP_ORDER.map((g) => ({ group: g, list: (areas || []).filter((a) => (a.group || "Bristol") === g) })).filter((g) => g.list.length);
  const areaCount = (areas || []).length;

  return (
    <footer data-testid="site-footer" className="bg-obsidian text-white border-t border-white/10">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <img src="/logo-nobg.webp" alt="NOOR Design & Build Ltd" className="h-28 md:h-36 w-auto" data-testid="footer-logo" />
          <p className="text-white/60 text-sm leading-relaxed mt-8 max-w-xs">
            {settings?.tagline || "Quality you can see, service you can trust."}
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <a
              href={telLink(settings)}
              onClick={() => track("phone_click", { location: "footer" })}
              data-testid="footer-call-link"
              className="flex items-center gap-3 text-white/80 hover:text-ember transition-colors duration-300"
            >
              <Phone size={14} strokeWidth={1.5} /> {settings?.phone_display || "07782 760 279"}
            </a>
            <a
              href={`mailto:${settings?.email || "info@noordb.co.uk"}`}
              onClick={() => track("email_click", { location: "footer" })}
              data-testid="footer-email-link"
              className="flex items-center gap-3 text-white/80 hover:text-ember transition-colors duration-300"
            >
              <Mail size={14} strokeWidth={1.5} /> {settings?.email || "info@noordb.co.uk"}
            </a>
          </div>
        </div>

        <nav aria-label="Footer explore">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ember mb-6">Explore</p>
          <ul className="space-y-3 text-sm">
            {[
              ["Projects", "/projects"],
              ["How We Work", "/how-we-work"],
              ["About NOOR", "/about"],
              ["Areas We Cover", "/areas-we-cover"],
              ["Cost Calculator", "/cost-calculator"],
              ["Insights", "/insights"],
              ["Request a Consultation", "/request-a-consultation"],
              ["Contact", "/contact"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="text-white/70 hover:text-white transition-colors duration-300" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Footer services">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ember mb-6">Services</p>
          <ul className="space-y-3 text-sm">
            {(services || []).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-white/70 hover:text-white transition-colors duration-300" data-testid={`footer-service-${s.slug}`}>
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ember mb-6">Start Your Project</p>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            Tell us about your home and plans — we respond personally, usually within one working day.
          </p>
          <div className="space-y-3">
            <Link
              to="/request-a-consultation"
              data-testid="footer-consultation-cta"
              className="group flex items-center justify-between border border-white/15 px-5 py-4 text-sm text-white/80 hover:text-white hover:border-ember transition-colors duration-300"
            >
              Request a consultation
              <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/cost-calculator"
              data-testid="footer-calculator-cta"
              className="group flex items-center justify-between border border-white/15 px-5 py-4 text-sm text-white/80 hover:text-white hover:border-ember transition-colors duration-300"
            >
              Estimate your project cost
              <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </div>

      <nav aria-label="Footer areas" className="border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-10 md:py-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ember shrink-0">Areas We Cover</p>
          <p className="text-sm text-white/50 leading-relaxed" data-testid="footer-areas-summary">
            {grouped.map((g) => g.group).join(" · ")}
          </p>
          <Link
            to="/areas-we-cover"
            className="group inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors duration-300 shrink-0 md:ml-auto"
            data-testid="footer-areas-all"
          >
            View all {areaCount || ""} areas
            <ArrowUpRight size={12} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </nav>

      <div className="border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-6 text-[11px] leading-relaxed text-white/30" data-testid="footer-registered-info">
          <p>NOOR Design &amp; Build Ltd · Registered in England and Wales · Company No. 16409807 · Registered Office: 3rd Floor, Old Stock Exchange, St Nicholas Street, Bristol BS1 1TG</p>
        </div>
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <p>© {year} {settings?.company_name || "NOOR Design & Build Ltd"}. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-white transition-colors duration-300" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/cookie-policy" className="hover:text-white transition-colors duration-300" data-testid="footer-cookie-link">Cookie Policy</Link>
            <Link to="/terms-of-use" className="hover:text-white transition-colors duration-300" data-testid="footer-terms-link">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
